/**
 * <ImportStepperBar /> — the 6-phase stepper inside ImportDashboard.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 10:
 *   Upload → Understanding → Conversation → Review → Commit → Done
 *
 * Visual states per the spec:
 *   completed → brand-teal w/ checkmark
 *   current   → brand-blue, slightly larger
 *   future    → muted gray, smaller
 *
 * Motion: 200ms checkmark scale-up when a phase completes (per
 * acceptance criterion 11). Honors prefers-reduced-motion via
 * the motion.ts helpers.
 *
 * RTL: flips automatically — the row uses logical end/start; the
 * connector lines use `ms-2`/`me-2`. Tailwind RTL plugin handles
 * the reorder.
 */

import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DURATION, EASE, withReducedMotion } from "../../lib/motion";

export type ImportPhase =
  | "upload"
  | "understanding"
  | "conversation"
  | "review"
  | "commit"
  | "done";

const PHASE_ORDER: ImportPhase[] = [
  "upload",
  "understanding",
  "conversation",
  "review",
  "commit",
  "done",
];

const PHASE_LABELS: Record<ImportPhase, { en: string; ar: string }> = {
  upload: { en: "Upload", ar: "رفع" },
  understanding: { en: "Understanding", ar: "فهم" },
  conversation: { en: "Conversation", ar: "محادثة" },
  review: { en: "Review", ar: "مراجعة" },
  commit: { en: "Commit", ar: "تنفيذ" },
  done: { en: "Done", ar: "تم" },
};

export interface ImportStepperBarProps {
  current: ImportPhase;
  locale?: "en" | "ar";
}

export function ImportStepperBar({
  current,
  locale = "en",
}: ImportStepperBarProps) {
  const { t } = useTranslation();
  const currentIdx = PHASE_ORDER.indexOf(current);

  return (
    <ol
      className="flex items-center gap-1 sm:gap-2"
      aria-label={t("app.isb_aria")}
    >
      {PHASE_ORDER.map((phase, idx) => {
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const label = PHASE_LABELS[phase][locale];

        const stateLabel = isCompleted
          ? `${label} (${t("app.stp_completed_aria")})`
          : isCurrent
          ? `${label} (${t("app.stp_current_aria")})`
          : `${label} (${t("app.stp_upcoming_aria")})`;

        const dotCls = isCompleted
          ? "bg-brand-teal text-white"
          : isCurrent
          ? "bg-brand-blue text-white ring-4 ring-brand-blue/15"
          : "bg-surface-sunken text-text-tertiary";

        const labelCls = isCompleted
          ? "text-text-primary"
          : isCurrent
          ? "font-semibold text-brand-deep"
          : "text-text-tertiary";

        const connectorCls =
          isCompleted || (isCurrent && idx > 0)
            ? "bg-brand-teal/50"
            : "bg-border-subtle";

        return (
          <li
            key={phase}
            className="flex items-center gap-1 sm:gap-2"
            aria-current={isCurrent ? "step" : undefined}
            aria-label={stateLabel}
          >
            {idx > 0 && (
              <span
                aria-hidden="true"
                className={`h-px w-3 sm:w-6 ${connectorCls}`}
              />
            )}
            <span className="flex items-center gap-1.5">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium transition-colors ${dotCls}`}
                aria-hidden="true"
              >
                {isCompleted ? (
                  <motion.span
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={withReducedMotion({
                      duration: DURATION.small / 1000,
                      ease: EASE.out,
                    })}
                  >
                    <Check size={12} strokeWidth={3} />
                  </motion.span>
                ) : (
                  idx + 1
                )}
              </span>
              <span className={`hidden text-[11px] sm:inline ${labelCls}`}>
                {label}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
